/* The L11 journey state machine — the order is the product (Bible v3.3 §B).
   entry → spin → sealed(+120s clock) → identity → invite → waiting → reveal → after
   The draw commits at spin; identity before any off-site link (Rule 2);
   nothing here may condition the reveal on a review (Rule 1). */

export type Step =
  | 'entry' | 'spin' | 'sealed' | 'identity' | 'invite'
  | 'waiting' | 'reveal' | 'after' | 'declined-wait'
  | 'paused' | 'lapsed'
  /* v4 closures + variants */
  | 'return-neutral' // CJ-08 · tab return, clock unchanged (accel OFF)
  | 'return-accel'   // CJ-09 · accelerated return (merchant opt-in)
  | 'ready'          // CJ-10 · hard deadline — the clock always wins
  | 'popup-blocked'  // CJ-11 · review tab failed to open
  | 'email-delayed'  // CJ-13 · e-mail late, wallet is the truth
  | 'marketing'      // CJ-03 · contextual opt-in AFTER the reveal
  | 'restored'       // CJ-32 · returned after the reveal — restore, no second spark
  | 'gift-first'     // CJ-15 · variant: reveal first, invitation second
  | 'quiz-intro' | 'quiz-q' | 'quiz-done' // CJ-16/17/18 · labelled first-party quiz
  | 'question'       // D-52 (06/09) · one question — choices or free text — skippable like the quiz
  | 'feedback'       // CJ-19 · private feedback, all sentiment accepted
  /* repeat scans & edge states (wf01 §E) + remaining variants */
  | 'returning'      // CJ-04 · known customer — Journey ledger resume, skips CJ-02
  | 'no-request'     // CJ-20 · gift-only variant — no public or private ask anywhere
  | 'cooldown'       // CJ-21 · 30-day window: existing gift + next unlock, never a refusal
  | 'resume'         // CJ-22 · 4-hour same-session resume — stored assignment, never a reroll
  | 'offline'        // CJ-24 · offline/error — gift-safety reassurance, no blame
  | 'age-gate'       // CJ-25 · min age/territory (CA-02 fields) — year-only, pass/fail
  | 'email-code'     // CJ-26 · 6-digit code · resend cooldown · expired auto-recovers
  | 'consent'        // CJ-27 · hosted-page cookie consent — refusal never breaks the journey
  | 'reveal-simple'  // CJ-28 · non-wheel reveal — same spark rules, no fake spin
  | 'merci'          // « Rien — la demande seule » (?cadeau=aucun): the demande, then this merci; no gift, no address

export const REVEAL_SECONDS = 120

export interface Gift {
  id: string; label: string; code: string
  validUntil: string; location: string
}

import { drawFromRoue, type Roue } from '@/lib/wheel'

/** Mock draw — server-side in production. With the allocated roue (?roue=, ruling
 *  04/09) it is a weighted draw over that roue's drawable types; without one it
 *  stays deterministic so verification screenshots are stable. */
export function draw(roue?: Roue, type?: { id: string; name: string } | null): Gift {
  /* I-7 · a direct gift (?cadeau=<typeId>): that one type, no draw */
  if (type) return { id: `gift_${type.id}`, label: type.name.charAt(0).toUpperCase() + type.name.slice(1), code: 'PRIZE-4K7T-9QX2', validUntil: '27 octobre 2026', location: 'Valessio — Paris 9' }
  const picked = roue ? drawFromRoue(roue) : null
  if (picked) return {
    id: `gift_${picked.rewardId}`, label: picked.name.charAt(0).toUpperCase() + picked.name.slice(1),
    code: 'PRIZE-4K7T-9QX2', validUntil: '27 octobre 2026',
    location: 'Valessio — Paris 9',
  }
  return {
    id: 'gift_demo1', label: 'Un soin profond offert',
    code: 'PRIZE-4K7T-9QX2', validUntil: '27 octobre 2026',
    location: 'Valessio — Paris 9',
  }
}

/** Steps reachable by URL (?etape=) for verification screenshots. */
export const STEPS: Step[] = [
  'entry', 'spin', 'sealed', 'identity', 'invite', 'waiting',
  'reveal', 'after', 'declined-wait', 'paused', 'lapsed',
  'return-neutral', 'return-accel', 'ready', 'popup-blocked',
  'email-delayed', 'marketing', 'restored', 'gift-first',
  'quiz-intro', 'quiz-q', 'quiz-done', 'question', 'feedback',
  'returning', 'no-request', 'cooldown', 'resume', 'offline',
  'age-gate', 'email-code', 'consent', 'reveal-simple', 'merci',
]
